/**
 * selly-price-sync.js — Helti custom (2026-08-28). Vezi CUSTOM_CHANGES.md, secțiunea N.
 *
 * Problema: aplicația SELLY ‑ Promotion & Pricing (Treedify) afișează prețul redus DOAR în
 * elementele ei — pe pagina de produs în widget-ul #tdf_sale_widget (injectat în formularul
 * produsului), pe cardurile de produs într-o etichetă .tdf_label peste imagine — iar tema afișează
 * în continuare prețul standard Shopify. Clientul vede două prețuri.
 *
 * Ce face:
 *  - PDP: citește prețul original + redus din widget-ul Selly și le afișează în <block-price>
 *    (preț vechi tăiat + preț redus), refolosind markup-ul și CSS-ul temei pentru starea „on sale”.
 *    Sincronizează și prețul din sticky ATC (mobil).
 *  - Carduri (.product-grid-item — colecții, căutare, homepage, recomandări): când Selly a pus
 *    eticheta cu prețul redus, rescrie .grid-product__price (preț redus + preț vechi tăiat).
 *  Nu afișează badge „-X%”: procentul nu se recalculează din sume deja rotunjite la ban (posibil ±1%
 *  față de oferta Selly); pe PDP procentul rămâne în mesajul ofertei Selly.
 *
 * ATENȚIE — integrare NEDOCUMENTATĂ de Selly: selectorii din `SELLY` sunt deduși din bundle-ul
 * lor de storefront (ac2_lt.js). Dacă Selly își schimbă widget-ul/eticheta, scriptul trebuie să
 * eșueze „silențios”: orice verificare picată => tema rămâne/revine la prețul standard Shopify.
 * Pe carduri Selly randează eticheta târziu/inconsistent (timer + scroll); asumat: unde Selly nu
 * pune eticheta, cardul rămâne exact ca fără acest script.
 * Principii:
 *   - nu aplică nimic dacă nu găsește prețurile, nu le poate parsa sau nu sunt în formatul temei;
 *   - nu aplică dacă prețul „original” Selly nu coincide cu prețul afișat de temă (PDP; widget vechi,
 *     variantă schimbată) sau dacă prețul redus nu e mai mic decât cel afișat de temă;
 *   - păstrează un snapshot al markup-ului original și îl restaurează când widget-ul/eticheta dispare
 *     (variantă fără discount, ofertă expirată, Selly dezinstalat);
 *   - tot codul e în try/catch; o eroare => log în consolă (cu ?selly_debug) și nimic modificat.
 * Se activează din Customize → Theme settings → „Integrare Selly” (settings.selly_price_sync).
 * Afișare doar: discountul în coș/checkout îl aplică Selly prin Shopify Functions, independent.
 */
(function () {
  'use strict';

  var cfg = (window.theme && window.theme.sellyPriceSync) || {};
  var DEBUG = !!cfg.debug || /[?&]selly_debug(=|&|$)/.test(window.location.search);

  // Selectori Selly (reverse-engineered, ac2_lt.js @ 2026-08-28). Nu e API public.
  var SELLY = {
    widget: '#tdf_sale_widget',
    original: '.tdf_price_original',
    sale: '.tdf_price_sales',
    label: '.tdf_label',          // eticheta de pe carduri (text = prețul redus sau „Product label” cu {price})
    labelStale: '.tdf_olal'       // eticheta veche, marcată de Selly la re-randare și ștearsă după ~400ms
  };

  // Selectori temă (Expanse v9: snippets/block-price.liquid, block-sticky-atc.liquid, product-grid-item.liquid).
  var THEME = {
    block: 'block-price',
    container: '.block-price__container',
    regularPrice: '.block-price__regular',
    saleWrap: '.block-price__sale',
    salePrice: '.block-price__sale > .product__price',
    saleCompare: '.block-price__sale s',
    stickyPrice: '.sticky-atc__price',
    card: '.product-grid-item',
    cardPrice: '.grid-product__price',
    cardCurrent: '.grid-product__price--current',
    cardOriginal: '.grid-product__price--original'
  };

  var ATTR = 'data-selly-sync';          // pe elementul rescris: cheie „orig|sale” (idempotență)
  var BODY_CLASS = 'selly-price-synced'; // pe <body>, doar cu opțiunea „ascunde prețurile din widget”
  var EPS = 0.011;                       // toleranță la rotunjire (bani)

  var snapshots = typeof WeakMap === 'function' ? new WeakMap() : null;
  var scheduled = false;
  var observer = null;

  function log() {
    if (!DEBUG || !window.console) return;
    console.debug.apply(console, ['[selly-price-sync]'].concat([].slice.call(arguments)));
  }

  /** "22,25 lei" | "1.234,56 lei" | "$1,234.56" -> număr; NaN dacă nu poate parsa. */
  function parseMoney(text) {
    if (text == null) return NaN;
    var s = String(text).replace(/[^\d.,]/g, '');
    if (!s) return NaN;
    var dec = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
    var intPart = s;
    var fracPart = '';
    if (dec !== -1) {
      intPart = s.slice(0, dec);
      fracPart = s.slice(dec + 1);
      if (fracPart.length === 3) { // "1.234" => separator de mii, nu decimale
        intPart += fracPart;
        fracPart = '';
      }
    }
    intPart = intPart.replace(/[.,]/g, '');
    var n = parseFloat(intPart + (fracPart ? '.' + fracPart : ''));
    return isFinite(n) ? n : NaN;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function escapeRegExp(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /** Reproduce formatarea snippet-ului `price` al temei (superscript la decimale, dacă tema o folosește). */
  function priceHtml(text, useSup) {
    var safe = escapeHtml(text);
    var visible = safe;
    if (useSup) {
      var m = /^(.*\d)([.,])(\d{2})(\D*)$/.exec(safe);
      if (m) visible = m[1] + '<sup>' + m[3] + '</sup>' + m[4];
    }
    return '<span aria-hidden="true">' + visible + '</span><span class="visually-hidden">' + safe + '</span>';
  }

  /** Textul vizibil al unui preț randat de temă (span-ul aria-hidden, fără duplicatul visually-hidden). */
  function visibleText(el) {
    if (!el) return '';
    var v = el.querySelector('[aria-hidden]');
    return ((v || el).textContent || '').trim();
  }

  var NUM = '(\\d[\\d.,\\s]*\\d|\\d)';

  /**
   * Regex care recunoaște un preț în formatul magazinului (același prefix/sufix de monedă), ca o
   * etichetă Selly „-50%” să nu fie luată drept preț. Sursa principală: `shop.money_format` transmis
   * din Liquid (ex. "{{amount_with_comma_separator}} lei"); fallback: prețul afișat de temă.
   */
  var moneyPatternCache;
  function moneyPattern(themeText) {
    if (moneyPatternCache === undefined) {
      moneyPatternCache = null;
      var fmt = String(cfg.moneyFormat || '').replace(/<[^>]*>/g, '');
      var parts = fmt.split(/\{\{\s*[a-z_]+\s*\}\}/);
      if (parts.length === 2 && (parts[0].trim() || parts[1].trim())) {
        moneyPatternCache = new RegExp(escapeRegExp(parts[0].trim()) + '\\s*' + NUM + '\\s*' + escapeRegExp(parts[1].trim()));
      }
    }
    if (moneyPatternCache) return moneyPatternCache;
    var m = new RegExp(NUM).exec(themeText || '');
    if (!m) return null;
    var prefix = themeText.slice(0, m.index).trim();
    var suffix = themeText.slice(m.index + m[0].length).trim();
    if (!prefix && !suffix) return null; // fără indiciu de monedă nu putem distinge un preț de alt număr
    return new RegExp(escapeRegExp(prefix) + '\\s*' + NUM + '\\s*' + escapeRegExp(suffix));
  }

  function getSnapshot(el) {
    if (!snapshots) return null;
    var snap = snapshots.get(el);
    if (!snap) {
      snap = { html: el.innerHTML, onSale: el.hasAttribute('data-on-sale') };
      snapshots.set(el, snap);
    }
    return snap;
  }

  function restoreEl(el) {
    var snap = snapshots && snapshots.get(el);
    if (!snap || !el.hasAttribute(ATTR)) return;
    el.innerHTML = snap.html;
    el.removeAttribute(ATTR);
    if (!snap.onSale) el.removeAttribute('data-on-sale');
  }

  /* ----------------------------------------------------------------------------------------- */
  /* PDP: widget-ul Selly -> <block-price> + sticky ATC                                          */
  /* ----------------------------------------------------------------------------------------- */

  /** Citește și validează prețurile din widget-ul Selly. null => nu avem date sigure. */
  function readSelly(widget) {
    var o = widget.querySelector(SELLY.original);
    var s = widget.querySelector(SELLY.sale);
    if (!o || !s) return null;
    var oText = (o.textContent || '').trim();
    var sText = (s.textContent || '').trim();
    var oVal = parseMoney(oText);
    var sVal = parseMoney(sText);
    if (!(oVal > 0) || !(sVal >= 0) || !(sVal < oVal - EPS)) {
      log('prețuri Selly invalide/neparsabile', oText, sText);
      return null;
    }
    return { originalText: oText, saleText: sText, original: oVal, sale: sVal, key: oText + '|' + sText };
  }

  /** Cel mai apropiat strămoș al widget-ului care conține un <block-price> (același produs). */
  function findBlock(widget) {
    var node = widget.parentElement;
    while (node && node !== document.body) {
      var block = node.querySelector(THEME.block);
      if (block) return block;
      node = node.parentElement;
    }
    return null;
  }

  /** Prețul curent afișat de temă (și compare-at, dacă există), din snapshot-ul nemodificat. */
  function readThemePrices(snap) {
    var tmp = document.createElement('div');
    tmp.innerHTML = snap.html;
    var cur = tmp.querySelector(snap.onSale ? THEME.salePrice : THEME.regularPrice);
    var cmp = snap.onSale ? tmp.querySelector(THEME.saleCompare) : null;
    return {
      current: parseMoney(visibleText(cur)),
      compare: parseMoney(cmp && cmp.textContent),
      useSup: /<sup>/i.test(snap.html)
    };
  }

  /** Aplică prețurile Selly în blocul temei. true = aplicat (sau deja aplicat). */
  function applyBlock(block, data) {
    var container = block.querySelector(THEME.container);
    if (!container || !snapshots) return false;
    if (container.getAttribute(ATTR) === data.key) return true; // deja sincronizat

    var snap = getSnapshot(container);
    var theme = readThemePrices(snap);
    if (!(theme.current > 0)) {
      log('nu pot parsa prețul temei');
      return false;
    }
    // Widget vechi (variantă schimbată) sau structură necunoscută => nu atingem prețul temei.
    var matchesTheme = Math.abs(data.original - theme.current) < EPS
      || (theme.compare > 0 && Math.abs(data.original - theme.compare) < EPS);
    if (!matchesTheme || !(data.sale < theme.current - EPS)) {
      log('prețul Selly nu corespunde cu tema (stale?)', data, theme);
      restoreEl(container);
      return false;
    }

    // Pornim de la markup-ul original ca să nu acumulăm modificări peste modificări.
    container.innerHTML = snap.html;
    var salePrice = container.querySelector(THEME.salePrice);
    var saleWrap = container.querySelector(THEME.saleWrap);
    if (!salePrice || !saleWrap) {
      log('markup-ul temei s-a schimbat (lipsește .block-price__sale)');
      container.removeAttribute(ATTR);
      return false;
    }

    salePrice.innerHTML = priceHtml(data.saleText, theme.useSup);

    var compare = container.querySelector(THEME.saleCompare);
    if (!compare) {
      var wrap = document.createElement('span');
      compare = document.createElement('s');
      compare.style.cssText = 'color:#96a4b6;font-weight:400;';
      wrap.appendChild(compare);
      saleWrap.appendChild(wrap);
    }
    compare.innerHTML = priceHtml(data.originalText, theme.useSup);

    // Fără badge „-X%” (vezi antet). Badge-ul Liquid al temei (din compare_at) e ascuns prin CSS.

    container.setAttribute('data-on-sale', '');
    container.setAttribute(ATTR, data.key);
    log('PDP aplicat', data.originalText, '->', data.saleText);
    return true;
  }

  /** Sticky ATC (mobil) din aceeași secțiune: preț vechi tăiat + preț redus. */
  function applySticky(block, data) {
    var section = block.closest('.shopify-section') || document;
    var el = section.querySelector(THEME.stickyPrice);
    if (!el || !snapshots) return;
    if (el.getAttribute(ATTR) === data.key) return;
    getSnapshot(el);
    el.innerHTML = '<s class="sticky-atc__price--compare">' + escapeHtml(data.originalText) + '</s> ' + escapeHtml(data.saleText);
    el.setAttribute(ATTR, data.key);
  }

  function restorePdp() {
    var synced = document.querySelectorAll(THEME.container + '[' + ATTR + '], ' + THEME.stickyPrice + '[' + ATTR + ']');
    for (var i = 0; i < synced.length; i++) restoreEl(synced[i]);
    document.body.classList.remove(BODY_CLASS);
  }

  function syncPdp() {
    var widget = document.querySelector(SELLY.widget);
    var data = widget && readSelly(widget);
    var block = data && findBlock(widget);
    if (!data || !block) {
      restorePdp();
      return;
    }
    if (applyBlock(block, data)) {
      applySticky(block, data);
      document.body.classList.toggle(BODY_CLASS, !!cfg.hideWidgetPrices);
    } else {
      document.body.classList.remove(BODY_CLASS);
    }
  }

  /* ----------------------------------------------------------------------------------------- */
  /* Carduri: eticheta Selly -> .grid-product__price                                            */
  /* ----------------------------------------------------------------------------------------- */

  /** Citește prețul redus din eticheta Selly a unui card, DOAR dacă e în formatul prețului temei. */
  // Black Friday 2026 (Helti, 03.11.2026): etichetele Selly sunt doar „-XX%” (fără {price}).
  // Prețul redus se calculează din prețul afișat de temă: preț × (100 − XX) / 100, rotunjit la ban,
  // la fel ca Selly în coș și ca secțiunea hl-bf-oferte. Etichetele fără „-XX%” (ex. „1+1”) => null.
  function percentFromLabel(text, themeText) {
    var p = /^-\s?(\d{1,2})\s?%$/.exec(text);
    if (!p) return null;
    var pct = parseInt(p[1], 10);
    var current = parseMoney(themeText);
    if (!(pct > 0 && pct < 100) || !(current > 0)) return null;
    var sale = Math.round(Math.round(current * 100) * (100 - pct) / 100) / 100; // în bani, ca Liquid
    var num = new RegExp(NUM).exec(themeText);
    if (!num) return null;
    var dec = num[0].indexOf(',') !== -1 ? ',' : '.';
    var parts = sale.toFixed(2).split('.');
    if (dec === ',') parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'); // 1.234,50 ca tema
    var saleText = themeText.slice(0, num.index) + parts[0] + dec + parts[1] + themeText.slice(num.index + num[0].length);
    return { saleText: saleText.trim(), sale: sale };
  }

  function readCardLabel(card, themeText) {
    var label = card.querySelector(SELLY.label + ':not(' + SELLY.labelStale + ')') || card.querySelector(SELLY.label);
    if (!label) return null;
    var re = moneyPattern(themeText);
    if (!re) return null;
    var text = (label.textContent || '').replace(/\s+/g, ' ').trim();
    var m = re.exec(text);
    if (!m) return percentFromLabel(text, themeText); // etichetă fără preț (ex. „-40%”, Black Friday 2026)
    var saleText = m[0].trim();
    var sale = parseMoney(saleText);
    if (!(sale >= 0)) return null;
    return { saleText: saleText, sale: sale };
  }

  function applyCard(card) {
    var priceEl = card.querySelector(THEME.cardPrice);
    if (!priceEl || !snapshots) return false;
    var snap = getSnapshot(priceEl);
    var tmp = document.createElement('div');
    tmp.innerHTML = snap.html;
    var themeText = visibleText(tmp.querySelector(THEME.cardCurrent));
    var themeCurrent = parseMoney(themeText);
    if (!(themeCurrent > 0)) return false;

    var data = readCardLabel(card, themeText);
    if (!data) {
      restoreEl(priceEl);
      card.removeAttribute(ATTR);
      return false;
    }
    if (!(data.sale < themeCurrent - EPS)) { // eticheta nu e mai mică decât prețul afișat => nu e o reducere de arătat
      restoreEl(priceEl);
      card.removeAttribute(ATTR);
      return false;
    }

    var key = themeText + '|' + data.saleText;
    if (priceEl.getAttribute(ATTR) !== key) {
      priceEl.innerHTML = snap.html;
      var current = priceEl.querySelector(THEME.cardCurrent);
      if (!current) {
        log('markup-ul cardului s-a schimbat (lipsește ' + THEME.cardCurrent + ')');
        return false;
      }
      var useSup = /<sup>/i.test(snap.html);
      current.innerHTML = priceHtml(data.saleText, useSup);
      // Prețul tăiat = prețul de la care reduce Selly (cel afișat de temă). Un compare-at existent
      // ar însemna 3 prețuri pe card, așa că îl înlocuim.
      var original = priceEl.querySelector(THEME.cardOriginal);
      if (!original) {
        original = document.createElement('span');
        original.className = 'grid-product__price--original';
        current.insertAdjacentElement('afterend', original);
      }
      original.innerHTML = priceHtml(themeText, useSup);
      priceEl.setAttribute(ATTR, key);
      card.setAttribute(ATTR, '');
      log('card aplicat', themeText, '->', data.saleText);
    }
    // Eticheta Selly de pe imagine rămâne vizibilă, chiar și când conține doar prețul (cerința
    // clientului, 2026-08-31); „ascunde prețurile” se aplică doar widget-ului de pe PDP.
    return true;
  }

  function syncCards() {
    if (cfg.cards === false) return;
    var labelled = document.querySelectorAll(THEME.card + ' ' + SELLY.label);
    var i;
    var seen = [];
    for (i = 0; i < labelled.length; i++) {
      var card = labelled[i].closest(THEME.card);
      if (card && seen.indexOf(card) === -1) {
        seen.push(card);
        applyCard(card);
      }
    }
    // Carduri sincronizate anterior a căror etichetă a dispărut => înapoi la prețul temei.
    var synced = document.querySelectorAll(THEME.card + '[' + ATTR + ']');
    for (i = 0; i < synced.length; i++) {
      if (seen.indexOf(synced[i]) === -1) {
        var priceEl = synced[i].querySelector(THEME.cardPrice);
        if (priceEl) restoreEl(priceEl);
        synced[i].removeAttribute(ATTR);
      }
    }
  }

  /* ----------------------------------------------------------------------------------------- */

  function restoreAll() {
    restorePdp();
    var synced = document.querySelectorAll(THEME.card + '[' + ATTR + ']');
    for (var i = 0; i < synced.length; i++) {
      var priceEl = synced[i].querySelector(THEME.cardPrice);
      if (priceEl) restoreEl(priceEl);
      synced[i].removeAttribute(ATTR);
    }
  }

  function sync() {
    scheduled = false;
    try {
      syncPdp();
      syncCards();
    } catch (err) {
      // Orice eroare => tema rămâne cu prețul standard; nu propagăm.
      if (window.console) console.warn('[selly-price-sync] dezactivat pe această pagină:', err);
      try { restoreAll(); } catch (e) { /* noop */ }
      if (observer) observer.disconnect();
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    // setTimeout, nu requestAnimationFrame: rAF nu rulează în tab-uri de fundal (pagina deschisă în
    // tab nou ar rămâne nesincronizată până la primul repaint).
    window.setTimeout(sync, 0);
  }

  function init() {
    if (!snapshots || typeof MutationObserver !== 'function') return; // browser prea vechi
    if (!document.querySelector(THEME.block) && !document.querySelector(THEME.card)) return; // nicio pagină cu prețuri
    // Selly injectează/re-randează widget-ul și etichetele asincron (timere, scroll); tema înlocuiește
    // containerul de preț la schimbarea variantei și grila la filtrare (AJAX); sticky ATC își rescrie
    // prețul. Observăm tot și re-sincronizăm (idempotent: dacă totul e la zi, nu se produce nicio
    // mutație => nu buclează).
    observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
